import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { tranformData } from "views/property-groups-configuration/utils";

interface Segment {
  id: number;
  name: string;
  title: string;
  count: number;
  parent_id: number | null;
  level: number;
  position: number;
  current_split_value: Record<string, unknown>[];
  has_children: boolean;
}

const sampleData: { segments: Segment[] } = {
  segments: [
    {
      id: 1,
      name: "",
      title: "All properties",
      count: 100,
      parent_id: null,
      level: 0,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 2,
      name: "",
      title: "RENTX",
      count: 50,
      parent_id: 1,
      level: 1,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 3,
      name: "",
      title: "Any other owner",
      count: 50,
      parent_id: 1,
      level: 1,
      position: 1,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 4,
      name: "",
      title: "Texas",
      count: 25,
      parent_id: 2,
      level: 2,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 5,
      name: "",
      title: "Others",
      count: 25,
      parent_id: 2,
      level: 2,
      position: 1,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 6,
      name: "",
      title: "Others",
      count: 50,
      parent_id: 3,
      level: 2,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 7,
      name: "",
      title: "Austin",
      count: 20,
      parent_id: 4,
      level: 3,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 8,
      name: "",
      title: "Others",
      count: 5,
      parent_id: 4,
      level: 3,
      position: 1,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 9,
      name: "",
      title: "Others",
      count: 25,
      parent_id: 5,
      level: 3,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 10,
      name: "",
      title: "Others",
      count: 50,
      parent_id: 6,
      level: 3,
      position: 0,
      current_split_value: [{}],
      has_children: true
    },
    {
      id: 11,
      name: "",
      title: "Zips 1",
      count: 10,
      parent_id: 7,
      level: 4,
      position: 0,
      current_split_value: [{}],
      has_children: false
    },
    {
      id: 12,
      name: "",
      title: "Others",
      count: 10,
      parent_id: 7,
      level: 4,
      position: 1,
      current_split_value: [{}],
      has_children: false
    },
    {
      id: 13,
      name: "",
      title: "Others",
      count: 5,
      parent_id: 8,
      level: 4,
      position: 0,
      current_split_value: [{}],
      has_children: false
    },
    {
      id: 14,
      name: "",
      title: "Others",
      count: 25,
      parent_id: 9,
      level: 4,
      position: 0,
      current_split_value: [{}],
      has_children: false
    },
    {
      id: 15,
      name: "",
      title: "Others",
      count: 50,
      parent_id: 10,
      level: 4,
      position: 0,
      current_split_value: [{}],
      has_children: false
    }
  ]
};

const levelMapping: Record<PropertyGroupSplitBy, number> = {
  [PropertyGroupSplitBy.OWNER]: 1,
  [PropertyGroupSplitBy.STATE]: 2,
  [PropertyGroupSplitBy.CITY]: 3,
  [PropertyGroupSplitBy.ZIP]: 4
};

export const generateExampleSankeyData = (
  splitBy: PropertyGroupSplitBy[],
  dictionary?: { properties: string }
) => {
  const selectedLevels = splitBy.map((item) => levelMapping[item]).sort();

  if (selectedLevels.length === 0)
    return tranformData({
      split_by: [],
      segments: [{ ...sampleData.segments[0], has_children: false }]
    });

  const result: { split_by: PropertyGroupSplitBy[]; segments: Segment[] } = {
    split_by: splitBy,
    segments: [{ ...sampleData.segments[0] }]
  };

  const levelMap: number[] = [];
  selectedLevels.forEach((level, index) => {
    levelMap[level] = index + 1;
  });

  const idMap: Record<number, number> = { 1: 1 };
  let nextId = 2;

  const findAncestorAtLevel = (
    segment: Segment,
    targetLevel: number
  ): Segment | undefined => {
    if (segment.level === targetLevel) return segment;
    if (segment.parent_id === null) return undefined;

    const parent = sampleData.segments.find((s) => s.id === segment.parent_id);
    if (!parent) return undefined;

    return findAncestorAtLevel(parent, targetLevel);
  };

  const addSegment = (
    segment: Segment,
    parentId: number,
    newLevel: number
  ): number => {
    const existingSegment = result.segments.find(
      (s) =>
        s.title === segment.title &&
        s.parent_id === parentId &&
        s.level === newLevel
    );

    if (existingSegment) {
      existingSegment.count += segment.count;
      idMap[segment.id] = existingSegment.id;
      return existingSegment.id;
    }

    const newSegment = { ...segment };
    newSegment.parent_id = parentId;
    newSegment.level = newLevel;
    idMap[segment.id] = nextId;
    newSegment.id = nextId++;
    result.segments.push(newSegment);
    return newSegment.id;
  };

  sampleData.segments.forEach((segment) => {
    if (segment.id === 1 || !selectedLevels.includes(segment.level)) return;

    const newLevel = levelMap[segment.level];
    if (!newLevel) return;

    let parentSegment: Segment | undefined;

    if (segment.parent_id === null) {
      addSegment(segment, 1, newLevel);
      return;
    }

    const parent = sampleData.segments.find((s) => s.id === segment.parent_id);
    if (!parent) return;

    let ancestorLevelToFind = -1;

    for (let i = selectedLevels.length - 1; i >= 0; i--)
      if (
        selectedLevels[i] < segment.level &&
        (selectedLevels.includes(parent.level) ||
          selectedLevels[i] < parent.level)
      ) {
        ancestorLevelToFind = selectedLevels[i];
        break;
      }

    if (ancestorLevelToFind !== -1) {
      parentSegment = findAncestorAtLevel(parent, ancestorLevelToFind);
      if (parentSegment && idMap[parentSegment.id]) {
        addSegment(segment, idMap[parentSegment.id], newLevel);
        return;
      }
    }

    if (selectedLevels.includes(parent.level) && idMap[parent.id]) {
      addSegment(segment, idMap[parent.id], newLevel);
      return;
    }

    addSegment(segment, 1, newLevel);
  });

  result.segments.forEach((segment) => {
    segment.has_children = result.segments.some(
      (s) => s.parent_id === segment.id
    );
  });

  return tranformData(result, dictionary);
};
